import type { TranslationAvailability, TranslatorBackend } from "@/main/translation/TranslatorBackend";

/**
 * Chrome/Edge's built-in on-device `Translator` API (SV-140 D2). Present only where the browser ships
 * it; feature-detected, never polyfilled, and no network MT stands behind it.
 *
 * `Translator.create()` needs a user gesture when a model must be fetched, which is why nothing here
 * runs before the reader presses Translate: availability is a query, creation happens on the press.
 */
type ChromeAvailability = "unavailable" | "downloadable" | "downloading" | "available";

type ChromeTranslatorInstance = {
    translate: (text: string) => Promise<string>;
    destroy?: () => void;
};

type ChromeTranslatorApi = {
    availability: (options: { sourceLanguage: string; targetLanguage: string }) => Promise<ChromeAvailability>;
    create: (options: { sourceLanguage: string; targetLanguage: string }) => Promise<ChromeTranslatorInstance>;
};

type NetworkInformationLike = { type?: string; saveData?: boolean };

export function getChromeTranslatorApi(): ChromeTranslatorApi | undefined {
    if (typeof self === "undefined") {
        return undefined;
    }
    const api = (self as unknown as { Translator?: Partial<ChromeTranslatorApi> }).Translator;
    if (!api || typeof api.availability !== "function" || typeof api.create !== "function") {
        return undefined;
    }
    return api as ChromeTranslatorApi;
}

export function hasChromeTranslator(): boolean {
    return getChromeTranslatorApi() !== undefined;
}

export function chromeTranslatorBackend(): TranslatorBackend | null {
    const api = getChromeTranslatorApi();
    if (!api) {
        return null;
    }
    // One instance per pair for the life of the page: a second translation of the same pair (a reply,
    // in wave 2) must not pay creation again.
    const instances = new Map<string, Promise<ChromeTranslatorInstance>>();
    const instanceFor = (source: string, target: string): Promise<ChromeTranslatorInstance> => {
        const key = `${source}>${target}`;
        let instance = instances.get(key);
        if (!instance) {
            instance = api.create({ sourceLanguage: source, targetLanguage: target });
            // A failed creation must not be cached, or Retry could never succeed.
            instance.catch(() => instances.delete(key));
            instances.set(key, instance);
        }
        return instance;
    };

    return {
        kind: "chrome",
        async availability(source: string, target: string): Promise<TranslationAvailability> {
            try {
                const status = await api.availability({ sourceLanguage: source, targetLanguage: target });
                if (status === "available" || status === "downloading") {
                    // "downloading": a model fetch the reader already agreed to is in flight.
                    return "available";
                }
                return status === "downloadable" ? "downloadable" : "unavailable";
            } catch {
                return "unavailable";
            }
        },
        async isMetered(): Promise<boolean> {
            const connection = (typeof navigator !== "undefined"
                ? (navigator as unknown as { connection?: NetworkInformationLike }).connection
                : undefined);
            if (!connection) {
                // Desktop Chrome without the Network Information API: almost always a fixed line. The
                // browser also shows its own progress for a model it fetches.
                return false;
            }
            return connection.type === "cellular" || connection.saveData === true;
        },
        async download(source: string, target: string): Promise<void> {
            await instanceFor(source, target);
        },
        async translate(source: string, target: string, texts: string[]): Promise<string[]> {
            const translator = await instanceFor(source, target);
            const translated: string[] = [];
            for (const text of texts) {
                translated.push(await translator.translate(text));
            }
            return translated;
        },
    };
}
